import { format, parseISO } from 'date-fns';
import haversine from 'haversine';
import type {
    AttributeOption,
    DispatchAreaObject,
    SensorAlertBundle,
    SensorAlertSummary,
    SensorPriorityDto,
    SensorView,
} from '@mark43/cad-api';
import { globalAttributes } from '../common/global-attributes';
import { isValidLatLng } from '../location/helpers';
import { snakeCaseToTitleCase } from '../common/helpers/utils';

/**
 * Dispatch clients run for whole shifts, so the alert log is capped rather than unbounded.
 * The cap is on the store, not the UI — the drawer and the camera panel both read from it.
 */
export const MAX_SENSOR_ALERTS = 200;

export function getSensorAlertSortTime(alert: SensorAlertBundle): number {
    const createdTime = alert.createdDateUtc ? Date.parse(alert.createdDateUtc) : Number.NaN;
    return Number.isNaN(createdTime) ? 0 : createdTime;
}

/**
 * Dedups by `alertKey`, sorts oldest-first and keeps the newest
 * {@link MAX_SENSOR_ALERTS}. Incoming alerts win over existing ones with the same key so a
 * redelivery carrying a longer-lived `imageUrl` replaces the older copy.
 */
export function mergeSensorAlerts(
    existingAlerts: SensorAlertBundle[],
    incomingAlerts: SensorAlertBundle[]
): SensorAlertBundle[] {
    const alertsByKey = new Map<string, SensorAlertBundle>();

    for (const alert of [...existingAlerts, ...incomingAlerts]) {
        alertsByKey.set(alert.alertKey, alert);
    }

    return [...alertsByKey.values()]
        .sort((a, b) => getSensorAlertSortTime(a) - getSensorAlertSortTime(b))
        .slice(-MAX_SENSOR_ALERTS);
}

/**
 * `ALERT_RULE_MOTION` -> `Motion`.
 *
 * Deliberately a transform rather than a lookup table: motion is the only alert type confirmed
 * from live captures, so an unrecognized type has to render as itself rather than fall through a
 * switch to nothing.
 */
export function formatSensorAlertType(alertType: string | undefined): string {
    if (!alertType) {
        return 'Alert';
    }
    return snakeCaseToTitleCase(alertType.replace(/^ALERT_RULE_/, '').toLowerCase());
}

/** The timestamp is optional and could be unparseable, so this never throws. */
export function formatSensorAlertTime(createdDateUtc: string | undefined): string {
    if (!createdDateUtc) {
        return 'Unknown time';
    }
    const parsed = parseISO(createdDateUtc);
    if (Number.isNaN(parsed.getTime())) {
        return 'Unknown time';
    }
    return format(parsed, 'MM/dd/yy HH:mm:ss');
}

/**
 * The values the camera map renderer has symbols for. An `iconName` outside this set silently
 * renders as the grey default square.
 */
export const CAMERA_ICON_NAMES = ['Live', 'Offline'] as const;
export type CameraIconName = (typeof CAMERA_ICON_NAMES)[number];

/**
 * The backend resolves vendor status to a boolean, so an unreachable or unknown camera and an
 * explicitly offline one both render the same Offline symbol.
 */
export function getCameraIconName(online: boolean | undefined): CameraIconName {
    return online ? 'Live' : 'Offline';
}

/**
 * Motion classification the vendor puts in `details.object_type` — `person` is the one observed so
 * far. `details` is an untyped passthrough of the vendor payload, so this reads defensively and
 * returns nothing rather than guessing when the key is absent or not a string.
 *
 * Display only. The backend redacts this from its diagnostics under a PII rule, so it must not
 * reach a log line or a Datadog push.
 */
export function getSensorAlertObjectType(alert: SensorAlertBundle): string | undefined {
    const objectType = alert.details?.['object_type'];
    return typeof objectType === 'string' && objectType.length > 0 ? objectType : undefined;
}

/**
 * Decimal degrees, matching how the map ledger renders coordinates. Rounded to six places (about
 * 10cm) so float noise from the vendor does not reach the panel.
 *
 * PII under CJIS, like the address beside it — fine to display, never to log.
 */
export function formatSensorCoordinates(
    latitude: number | undefined,
    longitude: number | undefined
): string | undefined {
    if (!isValidLatLng(latitude, longitude)) {
        return undefined;
    }
    const round = (value: number) => Number(value.toFixed(6));
    return `${round(latitude as number)}, ${round(longitude as number)}`;
}

/**
 * How the alert classifies itself: the event type (`Motion`), the detected object class
 * (`person`), and the vendor's alert *rule* name (`QA Demo`). All three are display-safe.
 *
 * Rendered as tags, but they are classifications rather than user-applied labels — worth keeping
 * straight if a real tagging feature ever lands alongside them.
 */
export function getSensorAlertTags(alert: SensorAlertBundle): string[] {
    return [
        formatSensorAlertType(alert.alertType),
        getSensorAlertObjectType(alert),
        alert.alertName,
    ].filter((tag): tag is string => !!tag);
}

/** Time only, for the dense recent-events list where the date is implied by "last 24 hours". */
export function formatSensorAlertClockTime(createdDateUtc: string | undefined): string {
    if (!createdDateUtc) {
        return '--:--';
    }
    const parsed = parseISO(createdDateUtc);
    return Number.isNaN(parsed.getTime()) ? '--:--' : format(parsed, 'HH:mm');
}

/** `13:41:08` — second precision, for a clip window, where the exact moment matters. */
export function formatSensorAlertClipTime(utc: string | undefined): string | undefined {
    if (!utc) {
        return undefined;
    }
    const parsed = parseISO(utc);
    return Number.isNaN(parsed.getTime()) ? undefined : format(parsed, 'HH:mm:ss');
}

/**
 * `09/21 09:41:54` — the event panel's Sensor Events tab, where linked events can span days, so
 * the date has to be shown and the year does not.
 */
export function formatSensorAlertMonthDayTime(utc: string | undefined): string | undefined {
    if (!utc) {
        return undefined;
    }
    const parsed = parseISO(utc);
    return Number.isNaN(parsed.getTime()) ? undefined : format(parsed, 'MM/dd HH:mm:ss');
}

/**
 * End of the alert's clip window. Verkada sends a single `created_at` for a motion alert, so this
 * is normally absent and a single timestamp is shown rather than a range — the key is read
 * defensively because `details` is an untyped passthrough of the vendor payload.
 */
export function getSensorAlertEndUtc(alert: SensorAlertBundle): string | undefined {
    const endUtc = alert.details?.['end_time'];
    return typeof endUtc === 'string' && endUtc.length > 0 ? endUtc : undefined;
}

/**
 * Length of the alert's clip window in whole seconds, or nothing when the vendor sends no end time
 * (the normal case for motion). Callers must not substitute a default — "8 s" on an alert whose
 * duration is unknown is a fabricated fact.
 */
export function getSensorAlertDurationSeconds(alert: SensorAlertBundle): number | undefined {
    const endUtc = getSensorAlertEndUtc(alert);
    if (!endUtc || !alert.createdDateUtc) {
        return undefined;
    }
    const startMs = Date.parse(alert.createdDateUtc);
    const endMs = Date.parse(endUtc);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
        return undefined;
    }
    return Math.round((endMs - startMs) / 1000);
}

/**
 * Position of an alert in a list, for the panel's "2 / 11" counter and its up/down navigation.
 * Returns -1 when the alert is no longer in the list, which happens once the capped log rolls past
 * it — the panel treats that as "no navigation" rather than jumping somewhere arbitrary.
 */
export function findSensorAlertIndex(
    alerts: SensorAlertBundle[],
    alertKey: string | undefined
): number {
    if (alertKey === undefined) {
        return -1;
    }
    return alerts.findIndex((alert) => alert.alertKey === alertKey);
}

/**
 * Vendor confidence for the detection, 0–1. Absent on every alert type confirmed so far, so the
 * tag is simply dropped rather than showing a made-up number.
 */
export function getSensorAlertConfidence(alert: SensorAlertBundle): number | undefined {
    const confidence = alert.details?.['confidence'];
    if (confidence === undefined) {
        return undefined;
    }
    const parsed = Number(confidence);
    return Number.isFinite(parsed) ? parsed : undefined;
}

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/**
 * `CAD Engineering · 399 Fresno St` — where a camera is, as a person would say it.
 *
 * Site plus the street line rather than the camera's own `name`: the vendor names cameras with an
 * opaque id (`9DTX-69MC-W7K6`), which is not a place. Falls back to the name when there is no
 * address, because an opaque id still beats nothing.
 */
export function formatSensorPlace(sensor: SensorView | undefined): string | undefined {
    if (!sensor) {
        return undefined;
    }
    const street = sensor.address?.split(',')[0]?.trim();
    return [sensor.site, street ?? sensor.name].filter(Boolean).join(' · ') || undefined;
}

/** Exactly nine tiles in the grid, as a 3 x 3. */
export const SENSOR_GRID_SIZE = 9;

const METERS_PER_FOOT = 0.3048;
const FEET_PER_MILE = 5280;
/** Feet under a tenth of a mile, miles to one decimal from there — the sensor panel design's rule. */
const MILE_THRESHOLD_FEET = FEET_PER_MILE / 10;

/** The narrowed shape `isMappableSensor` guarantees, plus what ranking adds. */
export type NearbySensor = SensorView & {
    externalId: string;
    latitude: number;
    longitude: number;
    distanceMeters: number;
    /** Compass point from the clicked location toward the sensor. */
    bearing: string;
    /** 1-based, by distance. Stays with a sensor even when the grid repeats it. */
    rank: number;
};

/** `120 ft` / `0.2 mi`, matching how the design stamps distance on each tile. */
export function formatSensorDistance(distanceMeters: number): string {
    const feet = distanceMeters / METERS_PER_FOOT;
    if (feet < MILE_THRESHOLD_FEET) {
        return `${Math.round(feet)} ft`;
    }
    return `${(feet / FEET_PER_MILE).toFixed(1)} mi`;
}

/**
 * Compass point from one coordinate toward another. Eight points for the same reason the camera's
 * own facing uses eight: more precision than the reading justifies is just harder to scan.
 */
export function getCompassBearing(
    from: { latitude: number; longitude: number },
    to: { latitude: number; longitude: number }
): string {
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
    const deltaLongitude = toRadians(to.longitude - from.longitude);
    const fromLatitude = toRadians(from.latitude);
    const toLatitude = toRadians(to.latitude);

    const y = Math.sin(deltaLongitude) * Math.cos(toLatitude);
    const x =
        Math.cos(fromLatitude) * Math.sin(toLatitude) -
        Math.sin(fromLatitude) * Math.cos(toLatitude) * Math.cos(deltaLongitude);
    const degrees = (Math.atan2(y, x) * 180) / Math.PI;

    return COMPASS_POINTS[Math.round(((degrees + 360) % 360) / 45) % COMPASS_POINTS.length];
}

/**
 * The sensors nearest a point, nearest first, capped at `limit`. Sensors without usable
 * coordinates are dropped rather than sorted to the end — there is no distance to rank them by.
 */
export function findNearestSensors(
    sensors: SensorView[],
    from: { latitude: number; longitude: number },
    limit: number
): NearbySensor[] {
    return sensors
        .filter(isMappableSensor)
        .map((sensor) => ({
            sensor,
            distanceMeters: haversine(
                from,
                { latitude: sensor.latitude, longitude: sensor.longitude },
                { unit: 'meter' }
            ),
        }))
        .sort((a, b) => a.distanceMeters - b.distanceMeters)
        .slice(0, limit)
        .map(({ sensor, distanceMeters }, index) => ({
            ...sensor,
            distanceMeters,
            bearing: getCompassBearing(from, {
                latitude: sensor.latitude,
                longitude: sensor.longitude,
            }),
            rank: index + 1,
        }));
}

/**
 * Pads a short list by cycling through it until the grid is full.
 *
 * DEMO HACK. Our demo departments have two or three cameras, and a 3 x 3 grid with six empty
 * holes does not show the feature. Each tile keeps the real sensor's rank and distance, so a
 * repeated tile is honest about which camera it is — it is just the same camera more than once.
 *
 * Delete this and render `nearestSensors` directly once a department has nine cameras. Callers
 * should treat the result as display-only: it contains duplicate `externalId`s, so it must not be
 * used to derive counts, event lists, or anything else where a duplicate would double-count.
 */
export function fillSensorGrid(
    nearestSensors: NearbySensor[],
    gridSize = SENSOR_GRID_SIZE
): NearbySensor[] {
    if (nearestSensors.length === 0) {
        return [];
    }
    return Array.from(
        { length: gridSize },
        (_unused, index) => nearestSensors[index % nearestSensors.length]
    );
}

export function isMappableSensor(
    sensor: SensorView
): sensor is SensorView & { externalId: string; latitude: number; longitude: number } {
    return (
        sensor.externalId !== undefined &&
        sensor.latitude !== undefined &&
        sensor.longitude !== undefined &&
        isValidLatLng(sensor.latitude, sensor.longitude)
    );
}

/** What a tag on the Sensor Event Card says about the event. */
export type SensorEventTagKind = 'classification' | 'meta';
export type SensorEventTag = { label: string; kind: SensorEventTagKind };

/**
 * `Person detected`, or the event type when the vendor names no object: `Motion detected`.
 *
 * Deliberately not the vendor's alert *rule* name (`QA Demo`). That names the configuration that
 * fired, not what happened, and the design renders it as a tag instead.
 */
export function getSensorEventTitle(alert: SensorAlertBundle): string {
    const objectType = getSensorAlertObjectType(alert);
    const subject = objectType
        ? snakeCaseToTitleCase(objectType)
        : formatSensorAlertType(alert.alertType);
    return `${subject} detected`;
}

/**
 * The card's tags, classifications first.
 *
 * Classifications are what the camera detected — the event type and the object class. The rule
 * name and the confidence describe the detection rather than classify it, so they are metadata.
 * Within each group the order is the payload's. A label repeated across groups (a rule named
 * after its own object class) is shown once, as the classification.
 */
export function getSensorEventTags(alert: SensorAlertBundle): SensorEventTag[] {
    const objectType = getSensorAlertObjectType(alert);
    const confidence = getSensorAlertConfidence(alert);

    const tags: SensorEventTag[] = [
        { label: formatSensorAlertType(alert.alertType), kind: 'classification' },
        ...(objectType
            ? [{ label: snakeCaseToTitleCase(objectType), kind: 'classification' as const }]
            : []),
        ...(alert.alertName ? [{ label: alert.alertName, kind: 'meta' as const }] : []),
        ...(confidence === undefined
            ? []
            : [{ label: `Conf ${confidence.toFixed(2)}`, kind: 'meta' as const }]),
    ];

    const seen = new Set<string>();
    return tags.filter(({ label }) => {
        const normalized = label.toLowerCase();
        if (seen.has(normalized)) {
            return false;
        }
        seen.add(normalized);
        return true;
    });
}

/**
 * `CAD Engineering, 99 Fresno St, Fresno CA` — site and address, and nothing else.
 *
 * The design rules out camera and device identifiers here, and for Verkada the camera's `name` is
 * one: an opaque id like `9DTX-69MC-W7K6`, which is not a place. PII under CJIS, like every
 * address in this feature — fine on screen, never in a log line.
 */
export function formatSensorEventLocation(sensor: SensorView | undefined): string | undefined {
    return [sensor?.site, sensor?.address].filter(Boolean).join(', ') || undefined;
}

/**
 * What a new event's location search starts from: the camera's street address where it has one,
 * because a real address resolves better than a coordinate pair, and `lat, lng` otherwise — the
 * same form the unit-location prefill searches with. It lands in the Location field for the
 * dispatcher to see, so the coordinates are rounded the way the panel shows them.
 *
 * PII under CJIS: fine in a search box, never in a log line.
 */
export function getSensorLocationQuery(sensor: SensorView | undefined): string | undefined {
    return (
        sensor?.address?.trim() || formatSensorCoordinates(sensor?.latitude, sensor?.longitude)
    );
}

/**
 * An alert's priority as a short label: `P1` for a bare number — how departments commonly name
 * CFS priorities, and on its own it reads as a count — and the department's label as-is otherwise.
 * Absent when the alert's type has no priority mapped on the sensors support page.
 */
export function formatSensorPriority(
    sensorPriority: SensorPriorityDto | undefined
): string | undefined {
    const label = sensorPriority?.label?.trim();
    if (!label) {
        return undefined;
    }
    return /^\d+$/.test(label) ? `P${label}` : label;
}

/**
 * Whether an alert's priority is CAD's Priority 1. `sensorPriority.value` is the department's own
 * CFS_PRIORITY attribute; rank comes from the global level it rolls up to (`parentAttributeId`),
 * the way the event queue and event search decide Priority 1.
 */
export function isSensorPriority1(
    sensorPriority: SensorPriorityDto | undefined,
    priorityOptions: Pick<AttributeOption, 'value' | 'parentAttributeId'>[]
): boolean {
    if (!sensorPriority) {
        return false;
    }
    const option = priorityOptions.find((candidate) => candidate.value === sensorPriority.value);
    return option?.parentAttributeId === globalAttributes.cfsPriorityGlobal.CFS_PRIORITY_1;
}

/**
 * The name of the dispatch area a camera sits in. The classification carries only the id, so the
 * name is looked up among the department's dispatch areas; a rename then shows without the camera
 * being reclassified. Absent when the camera is not classified yet, sits outside every drawn area,
 * or names an area missing from the list.
 */
export function getSensorDispatchAreaName(
    sensor: Pick<SensorView, 'classification'> | undefined,
    dispatchAreas: Pick<DispatchAreaObject, 'id' | 'name'>[]
): string | undefined {
    const dispatchAreaId = sensor?.classification?.dispatchAreaId;
    if (dispatchAreaId === undefined) {
        return undefined;
    }
    return dispatchAreas.find((dispatchArea) => dispatchArea.id === dispatchAreaId)?.name;
}

/**
 * The sensor events linked to one CAD event, newest first: what the event panel loaded with, plus
 * any link this window's store has been told about since.
 *
 * `hydratedAlerts` is `EventPanelMetadata.sensorAlerts`, the server's answer as of the panel's last
 * load or push. The server re-pushes an open event panel whenever one of its links changes — link,
 * unlink, or the save that created the event — so this is also what carries an unlink. It is taken
 * as given: a held alert's `linkedEvents` is only as fresh as the copy that last reached this
 * window, and letting a stale copy overrule the server once hid every link made by a save.
 *
 * The store adds what reached it after the panel's last push — the stream republishes an alert
 * when its links change, and a link response lands in the window that made it — and supplies its
 * full copy, with `details`, for any alert it holds.
 *
 * A hydrated alert the store does not hold is a summary, without `details`; it is given an empty
 * one so the card can render it, and shows its event type rather than the detected object.
 */
export function getEventLinkedSensorAlerts(
    eventId: number,
    hydratedAlerts: SensorAlertSummary[],
    heldAlerts: SensorAlertBundle[]
): SensorAlertBundle[] {
    const heldByKey = new Map(heldAlerts.map((alert) => [alert.alertKey, alert]));
    const linked = new Map<string, SensorAlertBundle>();

    for (const summary of hydratedAlerts) {
        linked.set(summary.alertKey, heldByKey.get(summary.alertKey) ?? { ...summary, details: {} });
    }
    for (const alert of heldAlerts) {
        if (alert.linkedEvents.some((link) => link.eventId === eventId)) {
            linked.set(alert.alertKey, alert);
        }
    }

    return [...linked.values()].sort((a, b) => getSensorAlertSortTime(b) - getSensorAlertSortTime(a));
}
